/**
 * 🔴 **WHAT THIS SESSION MAY DO, SO THE CHROME CAN STOP LYING** (D54).
 *
 * `GET /auth/whoami` with the chosen organisation. Scopes are a property of a person IN a company, so
 * this is re-asked whenever the company changes — a switcher that changed the data but not the doors
 * would be its own bug, and a worse one, because the doors would be the previous company's.
 *
 * 🔴 **Best-effort, and it fails OPEN.** A failure returns `known: false`, which hides nothing — the
 * gate is the guard, and a sidebar that empties itself because one request timed out would be a far
 * worse outcome than one that briefly offers a door that refuses.
 */
import { API_BASE_URL } from "@/api/base";
import { authFetch, type FetchLike } from "./http";
import type { HeldScopes } from "@/nav/navScopes";

export const WHOAMI_PATH = "/auth/whoami";
/** On the critical path of drawing the sidebar, and its absence is survivable. */
export const SCOPES_TIMEOUT_MS = 6_000;

interface WhoAmIBody {
  accessScopes?: unknown;
  platformScopes?: unknown;
}

const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []);

/** The scopes this session holds where it is standing, or `known: false` when they could not be read. */
export async function fetchScopes(fetchImpl?: FetchLike): Promise<HeldScopes> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), SCOPES_TIMEOUT_MS);
  try {
    const response = await authFetch(`${API_BASE_URL}${WHOAMI_PATH}`, { signal: abort.signal }, fetchImpl);
    if (!response.ok) return { access: [], platform: [], known: false };
    const body = (await response.json()) as WhoAmIBody;
    return { access: strings(body.accessScopes), platform: strings(body.platformScopes), known: true };
  } catch {
    return { access: [], platform: [], known: false };
  } finally {
    clearTimeout(timer);
  }
}
